import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { SPACER_HEIGHT_PX, SPACER_SIZES, normalizeSpacerSize, type SpacerSize } from './Spacer';

const SIZE_LABEL: Record<SpacerSize, string> = { sm: 'S', md: 'M', lg: 'L' };

interface SizePickerProps {
    readonly size: SpacerSize;
    readonly onPick: (size: SpacerSize) => void;
}

function SizePicker({ size, onPick }: SizePickerProps) {
    return (
        <span
            contentEditable={false}
            className="absolute right-0 top-1/2 flex -translate-y-1/2 items-center gap-0.5 rounded-full bg-white px-1 py-0.5 shadow-sm"
        >
            {SPACER_SIZES.map((candidate) => (
                <button
                    key={candidate}
                    type="button"
                    title={`Spacer size ${SIZE_LABEL[candidate]}`}
                    aria-pressed={candidate === size}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onPick(candidate)}
                    className={
                        'h-4 w-4 rounded-full text-[9px] font-semibold leading-none transition-colors ' +
                        (candidate === size
                            ? 'bg-gray-800 text-white'
                            : 'text-gray-500 hover:bg-gray-100')
                    }
                >
                    {SIZE_LABEL[candidate]}
                </button>
            ))}
        </span>
    );
}

/** Pure space when read-only; editing adds a hairline so the block is visible
 *  and a size picker once it is selected. */
export function SpacerView({ node, editor, selected, updateAttributes }: NodeViewProps) {
    const size = normalizeSpacerSize(node.attrs.size);
    const editable = editor.isEditable;

    return (
        <NodeViewWrapper
            className="relative"
            style={{ height: SPACER_HEIGHT_PX[size] }}
            data-spacer-size={size}
        >
            {editable && (
                <span
                    contentEditable={false}
                    className={
                        'pointer-events-none absolute inset-x-0 top-1/2 block h-px -translate-y-1/2 border-t border-dashed ' +
                        (selected ? 'border-gray-400' : 'border-gray-200')
                    }
                />
            )}
            {editable && selected && (
                <SizePicker size={size} onPick={(next) => updateAttributes({ size: next })} />
            )}
        </NodeViewWrapper>
    );
}
